import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useTranslation } from 'react-i18next';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { Autocomplete, IconButton, Typography } from '@totalsoft/rocket-ui';
import { StyledTableBodyCell, StyledTableRow } from 'components/common/Styles';
import { insuranceConventionItemTypeValidator } from 'features/insuranceConventions/edit/validators';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';
import FlexBox from 'components/common/FlexBox';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';

const InsuranceConventionItemTypeContainer = ({
  itemType,
  itemTypesDictionary,
  onAddOrUpdate,
  onDelete,
  onUpdateLocalItemType,
  onCancelAdd
}) => {
  const [validation, validate] = useDirtyFieldValidation(insuranceConventionItemTypeValidator);
  const editing = useMemo(() => itemType.editing || itemType.isNew, [itemType.editing, itemType.isNew]);
  const disabled = useMemo(() => itemType.disabled, [itemType.disabled]);
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);

  const updateLocalItemType = useCallback(
    (property, value) => {
      onUpdateLocalItemType(itemType.itemTypeId, property, value);
    },
    [itemType.itemTypeId, onUpdateLocalItemType]
  );

  const handleEdit = useCallback(() => updateLocalItemType('editing', true), [updateLocalItemType]);

  const handleCancelEdit = useCallback(() => {
    if (itemType.isNew) onCancelAdd();
    else updateLocalItemType('editing', false);
  }, [itemType.isNew, onCancelAdd, updateLocalItemType]);

  const handleAddOrUpdate = useCallback(
    input => {
      if (!validate(input)) return;

      onAddOrUpdate(input);
    },
    [onAddOrUpdate, validate]
  );

  const handleDelete = useCallback(() => {
    onDelete(itemType.productId, itemType.itemTypeId);
  }, [onDelete, itemType]);
  return (
    <StyledTableRow>
      {editing ? (
        <ItemTypeEditView
          itemType={itemType}
          validation={validation}
          itemTypesDictionary={itemTypesDictionary}
          disabled={disabled || !canWrite}
          onCancelEdit={handleCancelEdit}
          onAddOrUpdate={handleAddOrUpdate}
          onDelete={handleDelete}
        />
      ) : (
        <ItemTypeDetailsView itemType={itemType} disabled={disabled || !canWrite} onEdit={handleEdit} onDelete={handleDelete} />
      )}
    </StyledTableRow>
  );
};

const ItemTypeDetailsView = ({ itemType, disabled, onEdit, onDelete }) => {
  const { t } = useTranslation();

  return (
    <>
      <StyledTableBodyCell>
        <Typography>{itemType.itemTypeName}</Typography>
      </StyledTableBodyCell>
      <StyledTableBodyCell>
        <FlexBox gap={0.5} justifyContent='end'>
          <IconButton type='edit' color='primary' size='tiny' variant='text' onClick={onEdit} tooltip={t('Edit')} disabled={disabled} />
          <IconButton
            type='delete'
            color='primary'
            size='tiny'
            variant='text'
            onClick={onDelete}
            tooltip={t('Delete')}
            disabled={disabled}
          />
        </FlexBox>
      </StyledTableBodyCell>
    </>
  );
};

ItemTypeDetailsView.propTypes = {
  itemType: PropTypes.object,
  disabled: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func
};

const ItemTypeEditView = ({ itemType, validation, itemTypesDictionary, disabled, onAddOrUpdate, onDelete, onCancelEdit }) => {
  const { t } = useTranslation();

  const getLensValue = useCallback(
    () => ({
      ...itemType,
      initialItemTypeId: itemType.itemTypeId
    }),
    [itemType]
  );

  const [lens, dirtyInfo] = useChangeTrackingLens(getLensValue());
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  const handleAddOrUpdate = () => {
    onAddOrUpdate(lens |> get);
  };

  const itemTypeChange = useCallback(
    value => {
      set(lens.itemTypeId)(value.id);
      set(lens.itemTypeName)(value.name);
    },
    [lens.itemTypeId, lens.itemTypeName]
  );

  return (
    <>
      <StyledTableBodyCell>
        {
          <Autocomplete
            label={t('InsuranceConventions.Details.ItemTypes')}
            valueKey='id'
            labelKey='name'
            value={{ id: lens.itemTypeId |> get, name: lens.itemTypeName |> get }}
            onChange={itemTypeChange}
            options={itemTypesDictionary}
            error={!isValid(validation.itemTypeId)}
            helperText={getErrors(validation.itemTypeId)}
            disabled={disabled}
          />
        }
      </StyledTableBodyCell>
      <StyledTableBodyCell>
        <FlexBox gap={0.5} justifyContent='end'>
          <IconButton
            type='save'
            color='primary'
            size='tiny'
            variant='text'
            onClick={handleAddOrUpdate}
            tooltip={t('Save')}
            disabled={disabled || !isDirty}
          />
          <IconButton
            type='cancel'
            color='primary'
            size='tiny'
            variant='text'
            onClick={onCancelEdit}
            tooltip={t('Cancel')}
            disabled={disabled}
          />
          {!itemType.isNew && (
            <IconButton
              type='delete'
              color='primary'
              size='tiny'
              variant='text'
              onClick={onDelete}
              tooltip={t('Delete')}
              disabled={disabled}
            />
          )}
        </FlexBox>
      </StyledTableBodyCell>
    </>
  );
};

ItemTypeEditView.propTypes = {
  itemType: PropTypes.object,
  validation: PropTypes.object,
  itemTypesDictionary: PropTypes.array,
  disabled: PropTypes.bool,
  onAddOrUpdate: PropTypes.func,
  onDelete: PropTypes.func,
  onCancelEdit: PropTypes.func
};

InsuranceConventionItemTypeContainer.propTypes = {
  itemType: PropTypes.object,
  validation: PropTypes.object,
  itemTypesDictionary: PropTypes.array,
  onDelete: PropTypes.func,
  onAddOrUpdate: PropTypes.func,
  onUpdateLocalItemType: PropTypes.func,
  onCancelAdd: PropTypes.func
};

export default InsuranceConventionItemTypeContainer;
